import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth, isConsumerAccount } from "@/context/AuthContext";
import { CaseChoices } from "@/components/case/CaseChoices";
import { CaseCompareTable } from "@/components/case/CaseCompareTable";
import { Compass, Phone, Loader2, BookmarkPlus, CheckCircle2, MessageSquare } from "lucide-react";

const SaveCta = ({ token }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const here = `/compare/${token}?save=1`;
  const save = async () => {
    setState("saving"); setErr("");
    try { await api.post(`/consumer/shared-comparison/${token}/save`); setState("saved"); window.history.replaceState(null, "", `/compare/${token}`); }
    catch (e) { setState("idle"); setErr(formatApiError(e?.response?.data?.detail) || "Couldn't save. Please try again."); }
  };
  const member = isConsumerAccount(user);
  useEffect(() => {
    if (member && new URLSearchParams(window.location.search).get("save") === "1") save();
  }, [member]); // eslint-disable-line react-hooks/exhaustive-deps
  if (state === "saved") return (
    <div className="rounded-xl border border-[#ABEFC6] bg-[#ECFDF3] p-4 text-sm text-[#067647]" data-testid="shared-saved">
      <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" />Saved to My Nauticus</p>
      <Link to="/app/member" className="mt-1 inline-block underline" data-testid="shared-open-my-nauticus">Open My Nauticus</Link>
    </div>
  );
  return (
    <div className="rounded-xl border border-[#D9D6CC] bg-white p-4" data-testid="shared-save-cta">
      <p className="text-sm font-semibold text-[#0B192C]">Want to keep this comparison? (optional)</p>
      <p className="text-sm text-[#5A6B7B]">Save these plans to My Nauticus so you can come back to them any time.</p>
      {err && <p className="mt-1 text-sm text-[#B42318]" data-testid="shared-save-error">{err}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {member ? (
          <button type="button" onClick={save} disabled={state === "saving"} className="inline-flex h-11 items-center gap-2 rounded-full bg-[#0B192C] px-5 text-sm font-semibold text-white" data-testid="shared-save-btn">
            {state === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookmarkPlus className="h-4 w-4" />}Save to My Nauticus
          </button>
        ) : user ? (
          <p className="text-sm text-[#5A6B7B]" data-testid="shared-save-staff-note">Signed in as staff. Members can save this from their own account.</p>
        ) : (
          <>
            <button type="button" onClick={() => navigate(`/register?next=${encodeURIComponent(here)}`)} className="inline-flex h-11 items-center gap-2 rounded-full bg-[#0B192C] px-5 text-sm font-semibold text-white" data-testid="shared-save-btn">
              <BookmarkPlus className="h-4 w-4" />Save to My Nauticus
            </button>
            <Link to={`/login?next=${encodeURIComponent(here)}`} className="inline-flex h-11 items-center px-3 text-sm text-[#1E3E62] underline" data-testid="shared-save-login">I already have an account</Link>
          </>
        )}
      </div>
    </div>
  );
};

export const SharedCaseComparison = ({ token, data }) => {
  const s = data.snapshot || {};
  const { user, loading } = useAuth();
  const counted = React.useRef(false);
  useEffect(() => {
    if (loading || counted.current) return;
    counted.current = true;
    if (["advisor", "platform_admin"].includes(user?.role)) return;
    api.post(`/public/shared-comparison/${token}/view`).catch(() => {});
  }, [loading, user, token]);
  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="shared-comparison">
      <div className="bg-[#0B192C] px-6 py-6 text-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Compass className="h-6 w-6 text-[#D4AF37]" />
            <div>
              <p className="font-display text-xl font-bold" data-testid="shared-title">Your {s.plan_year} plan comparison{s.county ? ` · ${s.county} County, ${s.state}` : ""}</p>
              <p className="text-sm text-white/70" data-testid="shared-advisor">Prepared by {data.advisor_name}</p>
            </div>
          </div>
          {data.advisor_phone && (
            <div className="flex flex-wrap gap-2">
              <a href={`tel:${data.advisor_phone}`} className="inline-flex h-11 items-center gap-2 rounded-full border border-white/30 px-4 text-sm font-semibold" data-testid="shared-advisor-call"><Phone className="h-4 w-4" />Call {data.advisor_phone}</a>
              <a href={`sms:${data.advisor_phone}`} className="inline-flex h-11 items-center gap-2 rounded-full bg-[#D4AF37] px-4 text-sm font-semibold text-[#0B192C]" data-testid="shared-advisor-text"><MessageSquare className="h-4 w-4" />Text your advisor</a>
            </div>
          )}
        </div>
      </div>
      <div className="mx-auto max-w-5xl space-y-5 p-6">
        <SaveCta token={token} />
        <CaseChoices choices={s.choices || []} />
        <CaseCompareTable comparison={s.comparison} />
        <p className="max-w-3xl text-xs text-[#5A6B7B]" data-testid="shared-scope-note">{s.scope_note} This is factual plan information, not an enrollment.</p>
      </div>
    </div>
  );
};
